// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// userSetFields (#339) — unwrap a Mongo update document into the plain field map
// the Convex setUserById mutation takes.
//
// WHY THIS IS ITS OWN FILE (CLAUDE.md's extract-the-pure-helper rule): it sits on
// a mutation payload path whose failure mode is SILENT. Convex reserves
// `$`-prefixed field names, so a single `$` key anywhere in the args throws at
// ARGUMENT-ENCODING time — and mirrorMutationToConvex catches that throw by
// design ("never let a Convex failure break the Mongo write"), returning null
// while the caller reports success. One stray `$` key therefore discards the
// ENTIRE user write with no error anywhere. Tests import this exact function, so
// they cannot drift from what production runs (#284's lesson).
//
// The app's Mongo callers send `{ $set: {...} }` (UserDetailModal, the prefs
// autosave). Convex is not Mongo: the operator is a Mongo wire detail, so it is
// unwrapped here and re-applied server-side when Convex calls the mutation
// Lambda (plain JS -> JSON.stringify, never Convex-encoded).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Build the plain field map for Convex's setUserById from a Mongo update doc.
 *
 * @param {object|null|undefined} update `{ $set: {...} }`, or an already-plain
 *   field map (tolerated so a future caller can't accidentally double-wrap).
 * @returns {object} plain fields, guaranteed free of `$`-prefixed keys. Never
 *   null/undefined — an unusable input yields `{}` (a no-op merge) rather than
 *   throwing, matching the Lambda's tolerance of an empty `$set`.
 */
export function buildUserSetFields(update) {
  const candidate =
    update && typeof update === "object" && !Array.isArray(update) && update.$set
      ? update.$set
      : update;

  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return {};
  }

  // Drop any remaining `$` key. Only $set is used today, but a stray operator
  // ($unset, $inc) would throw at encode time and silently void the whole write —
  // dropping it degrades to "that one field isn't synced" instead. Dotted keys
  // ("a.b") are left alone: Convex accepts them, and Mongo dot-paths are
  // meaningful to the Lambda. ⚠️ Accepting is not APPLYING — #565: the server
  // merge must treat "a.b" as a nested path (convex/lib/applySetFields), or the
  // value lands in a junk top-level key while the Mongo side is correct.
  const fields = {};
  for (const key of Object.keys(candidate)) {
    if (!key.startsWith("$")) fields[key] = candidate[key];
  }
  return fields;
}
